import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Snackbar,
  Pagination,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  History as HistoryIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  MonetizationOn as MonetizationOnIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  CreditCard as CreditCardIcon,
  People as PeopleIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  FileDownload as FileDownloadIcon,
  Cancel as CancelSubscriptionIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useIsMobile } from '../../hooks/useResponsive';
import { useCurrencyRate } from '../../hooks/useCurrencyRate';
import PackageCard from '../../components/PackageCard';
import './SubscriptionPage.css';

interface SubscriptionHistoryItem {
  id: string;
  packageName: string;
  price: number;
  currency: string;
  status: 'active' | 'expired' | 'cancelled' | 'pending';
  startDate: string;
  endDate: string;
  paymentMethod: string;
  autoRenewal: boolean;
  orderId?: string;
  receiptDownloaded?: boolean;
  checkoutUrl?: string;
  billingPeriod?: string;
  recurringSubscriptionId?: string;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  period: string;
  features: string[];
  popular?: boolean;
  color: string;
  description?: string;
  billing_period?: string;
  billing_interval?: number;
  monthly_price?: number;
  yearly_price?: number;
}

const SubscriptionPage: React.FC = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<'plans' | 'history' | 'statistics'>('plans');
  const [subscriptionHistory, setSubscriptionHistory] = useState<SubscriptionHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [receiptMenuAnchor, setReceiptMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState<string | null>(null);

  const [clientPlans, setClientPlans] = useState<SubscriptionPlan[]>([]);
  const [masterPlans, setMasterPlans] = useState<SubscriptionPlan[]>([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Toggle states for billing period and currency
  const [billingPeriod, setBillingPeriod] = useState<'month' | 'year'>('month');
  const [displayCurrency, setDisplayCurrency] = useState<'USD' | 'UAH'>('USD');
  const { refetchRate } = useCurrencyRate();

  // Sync tab state with URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    
    if (tabParam === 'subscriptions' || tabParam === 'history') {
      setActiveTab('history');
    } else if (tabParam === 'statistics') {
      setActiveTab('statistics');
    } else {
      setActiveTab('plans');
    }
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const tabParam = urlParams.get('tab');
      
      if (tabParam === 'subscriptions' || tabParam === 'history') {
        setActiveTab('history');
      } else if (tabParam === 'statistics') {
        setActiveTab('statistics');
      } else {
        setActiveTab('plans');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Update URL when tab changes
  const updateTabUrl = (tab: 'plans' | 'history' | 'statistics') => {
    const url = new URL(window.location.href);
    if (tab === 'history') {
      url.searchParams.set('tab', 'subscriptions');
    } else if (tab === 'statistics') {
      url.searchParams.set('tab', 'statistics');
    } else {
      url.searchParams.delete('tab');
    }
    window.history.pushState({}, '', url.toString());
  };

  const handleTabChange = (tab: 'plans' | 'history' | 'statistics') => {
    setActiveTab(tab);
    updateTabUrl(tab);
  };

  useEffect(() => {
    fetchSubscriptionHistory();
    fetchClientPlans();
    fetchMasterPlans();
  }, []);

  // Refetch rate when currency changes to ensure latest rate
  useEffect(() => {
    if (displayCurrency === 'UAH') {
      refetchRate();
    }
  }, [displayCurrency]); // Remove refetchRate from dependencies

  // Auto-populate form with user data
  const fetchSubscriptionHistory = async (page: number = currentPage, limit: number = pageSize) => {
    try {
      setLoading(true);

      const response = await fetch(`/api/v1/client/subscriptions?page=${page}&limit=${limit}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch subscription history');
      }

      const data = await response.json();

      // Transform the data to match our interface
      const transformedData: SubscriptionHistoryItem[] = (data.subscriptions || []).map((sub: any) => ({
        id: sub.id,
        packageName: sub.packageName,
        price: sub.price,
        currency: sub.currency,
        status: sub.status,
        startDate: sub.startDate,
        endDate: sub.endDate || undefined,
        paymentMethod: sub.paymentMethod,
        autoRenewal: sub.autoRenewal,
        orderId: sub.orderId || undefined,
        checkoutUrl: sub.checkoutUrl || undefined,
        billingPeriod: sub.billingPeriod || undefined,
        recurringSubscriptionId: sub.recurringSubscriptionId || undefined,
      }));

      setSubscriptionHistory(transformedData);
      
      // Update pagination state
      if (data.pagination) {
        console.log('[SubscriptionPage] Pagination data:', data.pagination);
        console.log('[SubscriptionPage] Current state:', {
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalCount: data.pagination.totalCount,
          pageSize: data.pagination.limit
        });
        setCurrentPage(data.pagination.currentPage);
        setTotalPages(data.pagination.totalPages);
        setTotalCount(data.pagination.totalCount);
        setPageSize(data.pagination.limit);
      }
    } catch (error) {
      console.error('Failed to fetch subscription history:', error);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Помилка завантаження історії підписок' : 'Failed to load subscription history',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchClientPlans = async () => {
    try {
      // Determine API endpoint based on user role
      let apiEndpoint = '/api/v1/client/subscription-plans';
      if (user?.role === 'master') {
        apiEndpoint = '/api/v1/master/subscription-plans';
      } else if (user?.role === 'owner') {
        apiEndpoint = '/api/v1/owner/subscription-plans';
      }

      const response = await fetch(apiEndpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch subscription plans');
      }

      const packages = await response.json();

      // Transform the data to match our interface
      const transformedPlans: SubscriptionPlan[] = (packages || []).map((pkg: any, index: number) => {
        // Generate colors based on index
        const colors = ['#3498db', '#2ecc71', '#9b59b6', '#e74c3c', '#f39c12'];
        const color = colors[index % colors.length];

        // Create features based on package properties
        const features: string[] = [];

        // Split description by newlines to create separate features
        if (pkg.description) {
          const descriptionLines = pkg.description.split('\n').filter((line: string) => line.trim() !== '');
          features.push(...descriptionLines);
        }

        return {
          id: pkg.id,
          name: pkg.name,
          price: pkg.price,
          currency: pkg.currency,
          period: language === 'ua' ? 'місяць' : 'month',
          features: features,
          popular: pkg.is_popular || false, // Use actual is_popular field from backend
          color: color,
          description: pkg.description || '',
          target_audience: user?.role === 'master' ? 'masters' : user?.role === 'owner' ? 'owners' : 'clients',
        };
      });

      setClientPlans(transformedPlans);
    } catch (error) {
      console.error('Failed to fetch subscription plans:', error);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Помилка завантаження планів підписки' : 'Failed to load subscription plans',
        severity: 'error'
      });
    }
  };

  const fetchMasterPlans = async () => {
    try {
      const response = await fetch('/api/v1/master/subscription-plans', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch master subscription plans');
      }

      const packages = await response.json();

      // Transform the data to match our interface
      const transformedPlans: SubscriptionPlan[] = (packages || []).map((pkg: any, index: number) => {
        // Generate colors based on index
        const colors = ['#e74c3c', '#f39c12', '#9b59b6', '#3498db', '#2ecc71'];
        const color = colors[index % colors.length];

        // Create features based on package properties
        const features: string[] = [];

        // Split description by newlines to create separate features
        if (pkg.description) {
          const descriptionLines = pkg.description.split('\n').filter((line: string) => line.trim() !== '');
          features.push(...descriptionLines);
        }

        return {
          id: pkg.id,
          name: pkg.name,
          price: pkg.price,
          currency: pkg.currency,
          period: language === 'ua' ? 'місяць' : 'month',
          features: features,
          popular: pkg.is_popular || false,
          color: color,
          description: pkg.description || '',
          target_audience: 'masters',
        };
      });

      setMasterPlans(transformedPlans);
    } catch (error) {
      console.error('Failed to fetch master subscription plans:', error);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Помилка завантаження планів для майстрів' : 'Failed to load master subscription plans',
        severity: 'error'
      });
    }
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat(language === 'ua' ? 'uk-UA' : 'en-US', {
      style: 'currency',
      currency: currency,
    }).format(price);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const locale = language === 'ua' ? 'uk-UA' : 'en-US';
      
      // Check if time is set (not 00:00:00)
      const hasTime = date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0;
      
      if (hasTime) {
        // Show date with time
        return date.toLocaleString(locale, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } else {
        // Show only date
        return date.toLocaleDateString(locale);
      }
    } catch (error) {
      return '-';
    }
  };

  const calculateEndDate = (startDate: string, billingPeriod?: string) => {
    if (!billingPeriod) return null;
    
    try {
      const start = new Date(startDate);
      let endDate: Date;
      
      switch (billingPeriod.toLowerCase()) {
        case 'monthly':
        case 'month':
          endDate = new Date(start);
          endDate.setMonth(endDate.getMonth() + 1);
          break;
        case 'yearly':
        case 'year':
          endDate = new Date(start);
          endDate.setFullYear(endDate.getFullYear() + 1);
          break;
        default:
          return null;
      }
      
      // Subtract 1 day since subscription ends the day before
      endDate.setDate(endDate.getDate() - 1);
      
      // Set time to 23:59:59 to indicate end of day
      endDate.setHours(23, 59, 59, 999);
      
      return endDate.toISOString();
    } catch (error) {
      return null;
    }
  };

  const getStatusIcon = (status: SubscriptionHistoryItem['status']) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'expired':
        return <CancelIcon sx={{ color: 'warning.main' }} />;
      case 'cancelled':
        return <CancelIcon sx={{ color: 'error.main' }} />;
      case 'pending':
        return <PendingIcon sx={{ color: 'info.main' }} />;
      default:
        return <PendingIcon />;
    }
  };

  const getStatusLabel = (status: SubscriptionHistoryItem['status']) => {
    switch (status) {
      case 'active':
        return language === 'ua' ? 'Активний' : 'Active';
      case 'expired':
        return language === 'ua' ? 'Закінчився' : 'Expired';
      case 'cancelled':
        return language === 'ua' ? 'Скасовано' : 'Cancelled';
      case 'pending':
        return language === 'ua' ? 'Очікує' : 'Pending';
      default:
        return status;
    }
  };

  const getStatusColor = (status: SubscriptionHistoryItem['status']) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'expired':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleRefresh = () => {
    fetchSubscriptionHistory();
    setSnackbar({
      open: true,
      message: language === 'ua' ? 'Історія підписок оновлена' : 'Subscription history refreshed',
      severity: 'success'
    });
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    fetchSubscriptionHistory(value, pageSize);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page
    fetchSubscriptionHistory(1, newPageSize);
  };

  const handleExport = () => {
    setSnackbar({
      open: true,
      message: language === 'ua' ? 'Функціонал експорту буде додано' : 'Export functionality coming soon',
      severity: 'success'
    });
  };

  const handleDownloadReceipt = async (orderId: string) => {
    console.log('[SubscriptionPage] Starting receipt download for order:', orderId);

    try {
      console.log('[SubscriptionPage] Calling receipt API...');
      const response = await fetch(`/api/v1/payment/receipt/${orderId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });

      console.log('[SubscriptionPage] Receipt API response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[SubscriptionPage] Receipt API error:', errorData);
        throw new Error(errorData.error || 'Failed to download receipt');
      }

      console.log('[SubscriptionPage] Creating blob from response...');
      // Create blob from response and download
      const blob = await response.blob();
      console.log('[SubscriptionPage] Blob created, size:', blob.size);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt_${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('[SubscriptionPage] Receipt download completed for order:', orderId);

      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Чек завантажено' : 'Receipt downloaded',
        severity: 'success'
      });
    } catch (error) {
      console.error('[SubscriptionPage] Receipt download failed:', error);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Помилка завантаження чека' : 'Failed to download receipt',
        severity: 'error'
      });
    }
  };

  const handleViewLiqPayReceipt = (orderId: string) => {
    // Find the subscription with matching orderId to get checkoutUrl
    const subscription = subscriptionHistory.find(sub => sub.orderId === orderId);
    
    if (subscription?.checkoutUrl) {
      // Use the stored checkout URL from database
      console.log('[SubscriptionPage] Opening LiqPay receipt page using stored URL:', subscription.checkoutUrl);
      window.open(subscription.checkoutUrl, '_blank');
    } else {
      // Fallback - don't generate URL, show message instead
      console.log('[SubscriptionPage] No checkout URL available for order:', orderId);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'URL чека недоступний' : 'Receipt URL not available',
        severity: 'error'
      });
    }
    
    setReceiptMenuAnchor(null);
  };

  const handleReceiptMenuClick = (event: React.MouseEvent<HTMLElement>, orderId: string) => {
    setReceiptMenuAnchor(event.currentTarget);
    setSelectedOrderId(orderId);
  };

  const handleReceiptMenuClose = () => {
    setReceiptMenuAnchor(null);
    setSelectedOrderId(null);
  };

  const handleCancelSubscription = (subscriptionId: string) => {
    setSubscriptionToCancel(subscriptionId);
    setCancelDialogOpen(true);
  };

  const confirmCancelSubscription = async () => {
    if (!subscriptionToCancel) return;

    try {
      const response = await fetch(`/api/v1/payment/subscriptions/${subscriptionToCancel}/cancel`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }

      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Підписку успішно скасовано' : 'Subscription successfully cancelled',
        severity: 'success'
      });

      // Refresh subscription history
      fetchSubscriptionHistory();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      setSnackbar({
        open: true,
        message: language === 'ua' ? 'Помилка скасування підписки' : 'Failed to cancel subscription',
        severity: 'error'
      });
    } finally {
      setCancelDialogOpen(false);
      setSubscriptionToCancel(null);
    }
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setSubscriptionToCancel(null);
  };

  const handlePlanSelect = async (planId: string) => {
    // Initiate payment and redirect to LiqPay
    try {
      console.log('[SubscriptionPage] Initiating payment for plan:', planId);
      
      const response = await fetch('/api/v1/payment/initiate-subscription', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: planId,
          payment_method: 'card', // Default payment method
          billing_period: billingPeriod,
        }),
      });

      console.log('[SubscriptionPage] API response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[SubscriptionPage] API error response:', errorData);
        throw new Error(errorData.error || 'Failed to initiate payment');
      }

      const paymentData = await response.json();
      console.log('[SubscriptionPage] Payment data received:', paymentData);

      if (paymentData.checkout_url && paymentData.order_id) {
        // Store order_id in sessionStorage for success page
        sessionStorage.setItem('pending_order_id', paymentData.order_id);
        
        console.log('[SubscriptionPage] Payment initiation successful:');
        console.log('[SubscriptionPage]   - Order ID:', paymentData.order_id);
        console.log('[SubscriptionPage]   - Checkout URL:', paymentData.checkout_url);
        console.log('[SubscriptionPage]   - Current domain:', window.location.origin);
        
        // Open LiqPay in the same window
        window.location.href = paymentData.checkout_url;
      } else {
        throw new Error('No checkout URL or order ID received');
      }
    } catch (error) {
      console.error('[SubscriptionPage] Payment initiation failed:', error);
      setSnackbar({
        open: true,
        message: language === 'ua'
          ? 'Помилка ініціації платежу. Спробуйте ще раз.'
          : 'Payment initiation failed. Please try again.',
        severity: 'error'
      });
    }
  };


  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">
          {language === 'ua' ? 'Завантаження...' : 'Loading...'}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <div className="master-subscription-page">
        <div className="master-settings-card">
          <div className="master-header-row">
          <h1 className="master-page-title">
            <CreditCardIcon sx={{ color: 'primary.main', mr: 1 }} />
            {user?.role === 'master'
              ? (language === 'ua' ? 'Плани послуг для майстрів' : 'Service Plans for Masters')
              : user?.role === 'owner'
              ? (language === 'ua' ? 'Плани послуг для власників' : 'Service Plans for Owners')
              : (language === 'ua' ? 'Плани послуг для клієнтів' : 'Service Plans for Clients')
            }
          </h1>
          </div>

        {/* Tab Navigation */}
        <div className="master-tabs-navigation">
          <button
            className={`master-tab-button ${activeTab === 'plans' ? 'active' : ''}`}
            onClick={() => handleTabChange('plans')}
          >
            <CreditCardIcon sx={{ fontSize: 16, mr: 1 }} />
            <span className="master-tab-text">
              {language === 'ua' ? 'Плани' : 'Plans'}
            </span>
          </button>
          <button
            className={`master-tab-button ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => handleTabChange('history')}
          >
            <HistoryIcon sx={{ fontSize: 16, mr: 1 }} />
            <span className="master-tab-text">
              {language === 'ua' ? 'Історія' : 'History'}
            </span>
          </button>
          <button
            className={`master-tab-button ${activeTab === 'statistics' ? 'active' : ''}`}
            onClick={() => handleTabChange('statistics')}
          >
            <TimelineIcon sx={{ fontSize: 16, mr: 1 }} />
            <span className="master-tab-text">
              {language === 'ua' ? 'Статистика' : 'Statistics'}
            </span>
          </button>
        </div>

          <div className="master-subscription-container">
            {activeTab === 'plans' && (
              <>
                {/* Plans Section */}
                <div className="packages-content" style={{ padding: '24px' }}>
                  {/* Packages Grid */}
                  <Grid container spacing={3}>
                    {/* Client Plans Section */}
                    {clientPlans.length > 0 && user?.role !== 'master' && (
                      <>
                        <Grid item xs={12}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2,
                            mt: 1
                          }}>
                            {/* <Typography variant="h6" sx={{
                              color: 'success.main',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}>
                              <PeopleIcon />
                              {language === 'ua' ? 'Плани для клієнтів' : 'Plans for Clients'}
                            </Typography> */}
                            <Box sx={{
                              height: '2px',
                              flex: 1,
                              background: 'linear-gradient(90deg, #4caf50, transparent)',
                              borderRadius: 1
                            }} />
                          </Box>
                        </Grid>
                        {clientPlans.map((pkg) => (
                          <Grid item xs={12} sm={6} lg={4} key={`client-${pkg.id}`}>
                            <PackageCard
                              pkg={pkg}
                              billingPeriod={billingPeriod}
                              displayCurrency={displayCurrency}
                              onBillingPeriodChange={setBillingPeriod}
                              onDisplayCurrencyChange={setDisplayCurrency}
                              onSelect={handlePlanSelect}
                              selectButtonText={language === 'ua' ? 'Обрати план' : 'Select Plan'}
                            />
                          </Grid>
                        ))}
                      </>
                    )}

                    {/* Master Plans Section */}
                    {masterPlans.length > 0 && (
                      <>
                        <Grid item xs={12}>
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2,
                            mt: 3
                          }}>
                            {/* <Typography variant="h6" sx={{
                              color: 'warning.main',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}>
                              <BuildIcon />
                              {language === 'ua' ? 'Плани для майстрів' : 'Plans for Masters'}
                            </Typography> */}
                            <Box sx={{
                              height: '2px',
                              flex: 1,
                              background: 'linear-gradient(90deg, #ff9800, transparent)',
                              borderRadius: 1
                            }} />
                          </Box>
                        </Grid>
                        {masterPlans.map((pkg) => (
                          <Grid item xs={12} sm={6} lg={4} key={`master-${pkg.id}`}>
                            <PackageCard
                              pkg={pkg}
                              billingPeriod={billingPeriod}
                              displayCurrency={displayCurrency}
                              onBillingPeriodChange={setBillingPeriod}
                              onDisplayCurrencyChange={setDisplayCurrency}
                              onSelect={handlePlanSelect}
                              selectButtonText={language === 'ua' ? 'Обрати план' : 'Select Plan'}
                            />
                          </Grid>
                        ))}
                      </>
                    )}

                    {clientPlans.length === 0 && masterPlans.length === 0 && (
                      <Grid item xs={12}>
                        <div className="no-packages">
                          <PeopleIcon />
                          <h3>{language === 'ua' ? 'Плани не знайдено' : 'No plans found'}</h3>
                          <p>
                            {language === 'ua' ? 'Немає доступних планів підписки' : 'No subscription plans available'}
                          </p>
                        </div>
                      </Grid>
                    )}
                  </Grid>
                </div>

                {/* Benefits Section */}
                <div className="master-info-sections">
                  <div className="master-info-block master-benefits">
                    <h3>
                      {user?.role === 'master'
                        ? (language === 'ua' ? 'Переваги для майстрів' : 'Benefits for Masters')
                        : user?.role === 'owner'
                        ? (language === 'ua' ? 'Переваги для власників' : 'Benefits for Owners')
                        : (language === 'ua' ? 'Переваги для клієнтів' : 'Benefits for Clients')
                      }
                    </h3>
                    <div className="master-benefits-list">
                      {user?.role === 'master' ? (
                        <>
                          <div className="master-benefit-item">
                            <div className="master-benefit-icon">
                              <i className="bi bi-calendar-check"></i>
                            </div>
                            <div className="master-benefit-content">
                              <h4>{language === 'ua' ? 'Керування бронюваннями' : 'Booking Management'}</h4>
                              <p>{language === 'ua'
                                ? 'Ефективне управління бронюваннями та клієнтами'
                                : 'Efficient management of bookings and clients'
                              }</p>
                            </div>
                          </div>
                          <div className="master-benefit-item">
                            <div className="master-benefit-icon">
                              <i className="bi bi-graph-up"></i>
                            </div>
                            <div className="master-benefit-content">
                              <h4>{language === 'ua' ? 'Зростання бізнесу' : 'Business Growth'}</h4>
                              <p>{language === 'ua'
                                ? 'Інструменти для залучення нових клієнтів'
                                : 'Tools to attract new clients'
                              }</p>
                            </div>
                          </div>
                          <div className="master-benefit-item">
                            <div className="master-benefit-icon">
                              <i className="bi bi-tools"></i>
                            </div>
                            <div className="master-benefit-content">
                              <h4>{language === 'ua' ? 'Професійні інструменти' : 'Professional Tools'}</h4>
                              <p>{language === 'ua'
                                ? 'Все необхідне для ефективної роботи майстра'
                                : 'Everything needed for effective master work'
                              }</p>
                            </div>
                          </div>
                        </>
                      ) : user?.role === 'owner' ? (
                        <>
                          <div className="master-benefit-item">
                            <div className="master-benefit-icon">
                              <i className="bi bi-building"></i>
                            </div>
                            <div className="master-benefit-content">
                              <h4>{language === 'ua' ? 'Керування бізнесом' : 'Business Management'}</h4>
                              <p>{language === 'ua'
                                ? 'Повний контроль над вашими бізнесами та співробітниками'
                                : 'Full control over your businesses and employees'
                              }</p>
                            </div>
                          </div>
                          <div className="master-benefit-item">
                            <div className="master-benefit-icon">
                              <i className="bi bi-bar-chart"></i>
                            </div>
                            <div className="master-benefit-content">
                              <h4>{language === 'ua' ? 'Аналітика та звіти' : 'Analytics & Reports'}</h4>
                              <p>{language === 'ua'
                                ? 'Детальна статистика та аналітика для прийняття рішень'
                                : 'Detailed statistics and analytics for decision making'
                              }</p>
                            </div>
                          </div>
                          <div className="master-benefit-item">
                            <div className="master-benefit-icon">
                              <i className="bi bi-cash-coin"></i>
                            </div>
                            <div className="master-benefit-content">
                              <h4>{language === 'ua' ? 'Фінансовий контроль' : 'Financial Control'}</h4>
                              <p>{language === 'ua'
                                ? 'Моніторинг доходів та витрат по всіх бізнесах'
                                : 'Monitoring income and expenses across all businesses'
                              }</p>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="master-benefit-item">
                            <div className="master-benefit-icon">
                              <i className="bi bi-calendar-check"></i>
                            </div>
                            <div className="master-benefit-content">
                              <h4>{language === 'ua' ? 'Легке бронювання' : 'Easy Booking'}</h4>
                              <p>{language === 'ua'
                                ? 'Швидке та зручне бронювання послуг у будь-який час'
                                : 'Fast and convenient booking of services at any time'
                              }</p>
                            </div>
                          </div>
                          <div className="master-benefit-item">
                            <div className="master-benefit-icon">
                              <i className="bi bi-people"></i>
                            </div>
                            <div className="master-benefit-content">
                              <h4>{language === 'ua' ? 'Найкращі майстри' : 'Best Masters'}</h4>
                              <p>{language === 'ua'
                                ? 'Доступ до перевірених професіоналів у вашому місті'
                                : 'Access to verified professionals in your city'
                              }</p>
                            </div>
                          </div>
                          <div className="master-benefit-item">
                            <div className="master-benefit-icon">
                              <i className="bi bi-star"></i>
                            </div>
                            <div className="master-benefit-content">
                              <h4>{language === 'ua' ? 'Особливі пропозиції' : 'Special Offers'}</h4>
                              <p>{language === 'ua'
                                ? 'Ексклюзивні знижки та пропозиції для підписників'
                                : 'Exclusive discounts and offers for subscribers'
                              }</p>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'history' && (
              <div className="master-faq-section">
                <Box sx={{ p: 2 }}>
                  {/* Header */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HistoryIcon sx={{ color: 'primary.main' }} />
                      {language === 'ua' ? 'Історія підписок' : 'Subscription History'}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {language === 'ua'
                        ? 'Перегляньте історію всіх ваших підписок та платежів'
                        : 'View the history of all your subscriptions and payments'
                      }
                    </Typography>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      startIcon={<RefreshIcon />}
                      onClick={handleRefresh}
                      sx={{
                        '& .MuiButton-startIcon': {
                          marginRight: { xs: 0, sm: '8px' }
                        }
                      }}
                    >
                      <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        {language === 'ua' ? 'Оновити' : 'Refresh'}
                      </Box>
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<TimelineIcon />}
                      onClick={handleExport}
                      sx={{
                        '& .MuiButton-startIcon': {
                          marginRight: { xs: 0, sm: '8px' }
                        }
                      }}
                    >
                      <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                        {language === 'ua' ? 'Експорт' : 'Export'}
                      </Box>
                    </Button>
                  </Box>

                  {/* Subscription History Table */}
                  <Card>
                    <CardContent>
                      {subscriptionHistory.length === 0 ? (
                        <Box sx={{
                          textAlign: 'center',
                          py: 6,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          border: '2px dashed #bdbdbd'
                        }}>
                          <HistoryIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            {language === 'ua' ? 'Немає історії підписок' : 'No subscription history'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {language === 'ua'
                              ? 'У вас ще немає підписок'
                              : 'You don\'t have any subscriptions yet'
                            }
                          </Typography>
                        </Box>
                      ) : (
                        <TableContainer component={Paper} elevation={0}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>{language === 'ua' ? 'Пакет' : 'Package'}</TableCell>
                                <TableCell align="right">{language === 'ua' ? 'Ціна' : 'Price'}</TableCell>
                                <TableCell>{language === 'ua' ? 'Статус' : 'Status'}</TableCell>
                                <TableCell>{language === 'ua' ? 'Початок' : 'Start Date'}</TableCell>
                                <TableCell>{language === 'ua' ? 'Кінець' : 'End Date'}</TableCell>
                                <TableCell>{language === 'ua' ? 'Оплата' : 'Payment'}</TableCell>
                                <TableCell>{language === 'ua' ? 'Автопродовження' : 'Auto Renewal'}</TableCell>
                                <TableCell>{language === 'ua' ? 'Чек' : 'Receipt'}</TableCell>
                                <TableCell>{language === 'ua' ? 'Дії' : 'Actions'}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {subscriptionHistory.map((subscription) => (
                                <TableRow key={subscription.id} hover>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32 }}>
                                        <MonetizationOnIcon sx={{ fontSize: 16 }} />
                                      </Avatar>
                                      <Typography variant="body2" fontWeight={500}>
                                        {subscription.packageName}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" fontWeight={500}>
                                      {formatPrice(subscription.price, subscription.currency)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {getStatusIcon(subscription.status)}
                                      <Chip
                                        label={getStatusLabel(subscription.status)}
                                        color={getStatusColor(subscription.status) as any}
                                        size="small"
                                        variant="outlined"
                                      />
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                      <Typography variant="body2">
                                        {formatDate(subscription.startDate)}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {formatDate(subscription.endDate || calculateEndDate(subscription.startDate, subscription.billingPeriod))}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {subscription.paymentMethod}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={subscription.autoRenewal
                                        ? (language === 'ua' ? 'Так' : 'Yes')
                                        : (language === 'ua' ? 'Ні' : 'No')
                                      }
                                      color={subscription.autoRenewal ? 'success' : 'default'}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {subscription.orderId && (
                                      <>
                                        <Button
                                          variant={subscription.receiptDownloaded ? "contained" : "outlined"}
                                          size="small"
                                          onClick={(e) => handleReceiptMenuClick(e, subscription.orderId!)}
                                          sx={{ minWidth: 'auto' }}
                                          color={subscription.receiptDownloaded ? "success" : "primary"}
                                          startIcon={<FileDownloadIcon />}
                                        >
                                          {language === 'ua' ? 'Чек' : 'Receipt'}
                                        </Button>
                                        
                                        <Menu
                                          anchorEl={receiptMenuAnchor}
                                          open={Boolean(receiptMenuAnchor) && selectedOrderId === subscription.orderId}
                                          onClose={handleReceiptMenuClose}
                                          anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'right',
                                          }}
                                          transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                          }}
                                        >
                                          <MenuItem onClick={() => {
                                            if (selectedOrderId) {
                                              handleDownloadReceipt(selectedOrderId);
                                            }
                                            handleReceiptMenuClose();
                                          }}>
                                            <ListItemIcon>
                                              <DownloadIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>
                                              {language === 'ua' ? 'Завантажити чек' : 'Download receipt'}
                                            </ListItemText>
                                          </MenuItem>
                                          <MenuItem onClick={() => {
                                            if (selectedOrderId) {
                                              handleViewLiqPayReceipt(selectedOrderId);
                                            }
                                            handleReceiptMenuClose();
                                          }}>
                                            <ListItemIcon>
                                              <OpenInNewIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>
                                              {language === 'ua' ? 'Переглянути на LiqPay' : 'View on LiqPay'}
                                            </ListItemText>
                                          </MenuItem>
                                        </Menu>
                                      </>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {subscription.status === 'active' && subscription.autoRenewal && subscription.recurringSubscriptionId && (
                                      <Tooltip 
                                        title={language === 'ua' ? 'Скасувати підписку' : 'Cancel subscription'}
                                        arrow
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={() => handleCancelSubscription(subscription.recurringSubscriptionId!)}
                                          sx={{
                                            color: 'error.main',
                                            '&:hover': {
                                              backgroundColor: 'error.light',
                                              color: 'error.contrastText',
                                            }
                                          }}
                                        >
                                          <CancelSubscriptionIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                      
                      {/* Pagination Controls */}
                      {(subscriptionHistory.length > 0 || (totalCount > 0 && totalPages > 1)) && (subscriptionHistory.length >= pageSize || totalPages > 1) && (
                        <Box sx={{ 
                          mt: 3, 
                          px: 2 
                        }}>
                          {/* Mobile Layout - Vertical Stack */}
                          {isMobile ? (
                            <Stack spacing={2} alignItems="center">
                              <Typography variant="body2" color="text.secondary" textAlign="center">
                                {language === 'ua' 
                                  ? `Показано ${subscriptionHistory.length} з ${totalCount} записів`
                                  : `Showing ${subscriptionHistory.length} of ${totalCount} records`
                                }
                              </Typography>
                              
                              <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                showFirstButton
                                showLastButton
                                size="small"
                                siblingCount={0}
                                boundaryCount={1}
                              />
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {language === 'ua' ? 'Записів на сторінці:' : 'Items per page:'}
                                </Typography>
                                <Button
                                  size="small"
                                  variant={pageSize === 10 ? 'contained' : 'outlined'}
                                  onClick={() => handlePageSizeChange(10)}
                                  sx={{ minWidth: 'auto', px: 1, fontSize: '0.75rem' }}
                                >
                                  10
                                </Button>
                                <Button
                                  size="small"
                                  variant={pageSize === 25 ? 'contained' : 'outlined'}
                                  onClick={() => handlePageSizeChange(25)}
                                  sx={{ minWidth: 'auto', px: 1, fontSize: '0.75rem' }}
                                >
                                  25
                                </Button>
                                <Button
                                  size="small"
                                  variant={pageSize === 50 ? 'contained' : 'outlined'}
                                  onClick={() => handlePageSizeChange(50)}
                                  sx={{ minWidth: 'auto', px: 1, fontSize: '0.75rem' }}
                                >
                                  50
                                </Button>
                              </Box>
                            </Stack>
                          ) : (
                            /* Desktop Layout - Horizontal Flex */
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center'
                            }}>
                              <Typography variant="body2" color="text.secondary">
                                {language === 'ua' 
                                  ? `Показано ${subscriptionHistory.length} з ${totalCount} записів`
                                  : `Showing ${subscriptionHistory.length} of ${totalCount} records`
                                }
                              </Typography>
                              
                              <Stack spacing={2} alignItems="center">
                                <Pagination
                                  count={totalPages}
                                  page={currentPage}
                                  onChange={handlePageChange}
                                  color="primary"
                                  showFirstButton
                                  showLastButton
                                  size="medium"
                                />
                              </Stack>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {language === 'ua' ? 'Записів на сторінці:' : 'Items per page:'}
                                </Typography>
                                <Button
                                  size="small"
                                  variant={pageSize === 10 ? 'contained' : 'outlined'}
                                  onClick={() => handlePageSizeChange(10)}
                                  sx={{ minWidth: 'auto', px: 1 }}
                                >
                                  10
                                </Button>
                                <Button
                                  size="small"
                                  variant={pageSize === 25 ? 'contained' : 'outlined'}
                                  onClick={() => handlePageSizeChange(25)}
                                  sx={{ minWidth: 'auto', px: 1 }}
                                >
                                  25
                                </Button>
                                <Button
                                  size="small"
                                  variant={pageSize === 50 ? 'contained' : 'outlined'}
                                  onClick={() => handlePageSizeChange(50)}
                                  sx={{ minWidth: 'auto', px: 1 }}
                                >
                                  50
                                </Button>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </div>
            )}

            {activeTab === 'statistics' && (
              <div className="master-faq-section">
                <Box sx={{ p: 2 }}>
                  {/* Header */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TimelineIcon sx={{ color: 'primary.main' }} />
                      {language === 'ua' ? 'Статистика платежів' : 'Payment Statistics'}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {language === 'ua'
                        ? 'Детальна статистика по всіх ваших платежах та підписках'
                        : 'Detailed statistics of all your payments and subscriptions'
                      }
                    </Typography>
                  </Box>

                  {/* Statistics Cards */}
                  <Grid container spacing={3}>
                    {/* Total Card */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        height: '100%'
                      }}>
                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                          <MonetizationOnIcon sx={{ fontSize: 48, mb: 1, opacity: 0.8 }} />
                          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                            {totalCount || 0}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {language === 'ua' ? 'Всього записів' : 'Total Records'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Active Card */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ 
                        background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                        color: 'white',
                        height: '100%'
                      }}>
                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                          <CheckCircleIcon sx={{ fontSize: 48, mb: 1, opacity: 0.8 }} />
                          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                            {(() => {
                              const stats = subscriptionHistory.length > 0 ? 
                                subscriptionHistory.reduce((acc, sub) => {
                                  if (sub.status === 'active') acc.active++;
                                  return acc;
                                }, { active: 0 }) : 
                                { active: 0 };
                              return stats.active;
                            })()}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {language === 'ua' ? 'Активні' : 'Active'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Pending Card */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ 
                        background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                        color: 'white',
                        height: '100%'
                      }}>
                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                          <PendingIcon sx={{ fontSize: 48, mb: 1, opacity: 0.8 }} />
                          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                            {(() => {
                              const stats = subscriptionHistory.length > 0 ? 
                                subscriptionHistory.reduce((acc, sub) => {
                                  if (sub.status === 'pending') acc.pending++;
                                  return acc;
                                }, { pending: 0 }) : 
                                { pending: 0 };
                              return stats.pending;
                            })()}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {language === 'ua' ? 'Очікують' : 'Pending'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Expired Card */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ 
                        background: 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)',
                        color: 'white',
                        height: '100%'
                      }}>
                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                          <CancelIcon sx={{ fontSize: 48, mb: 1, opacity: 0.8 }} />
                          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                            {(() => {
                              const stats = subscriptionHistory.length > 0 ? 
                                subscriptionHistory.reduce((acc, sub) => {
                                  if (sub.status === 'expired') acc.expired++;
                                  return acc;
                                }, { expired: 0 }) : 
                                { expired: 0 };
                              return stats.expired;
                            })()}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {language === 'ua' ? 'Закінчились' : 'Expired'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Total Amount Card */}
                    <Grid item xs={12}>
                      <Card sx={{ 
                        background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                        color: 'white'
                      }}>
                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                          <Typography variant="h6" gutterBottom sx={{ opacity: 0.9 }}>
                            {language === 'ua' ? 'Загальна сума платежів' : 'Total Payment Amount'}
                          </Typography>
                          <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                            ${(() => {
                              const totalAmount = subscriptionHistory.length > 0 ? 
                                subscriptionHistory.reduce((acc, sub) => acc + sub.price, 0) : 
                                0;
                              return totalAmount.toFixed(2);
                            })()}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        sx={{ width: '100%' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Cancel Subscription Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={closeCancelDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CancelSubscriptionIcon color="error" />
          {language === 'ua' ? 'Скасувати підписку' : 'Cancel Subscription'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            {language === 'ua' 
              ? 'Ви впевнені, що хочете скасувати цю підписку?'
              : 'Are you sure you want to cancel this subscription?'
            }
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {language === 'ua'
              ? 'Після скасування автопродовження ваша підписка залишиться активною до кінця поточного періоду, але не буде автоматично продовжена.'
              : 'After cancelling auto-renewal, your subscription will remain active until the end of the current period, but will not be automatically renewed.'
            }
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={closeCancelDialog} variant="outlined">
            {language === 'ua' ? 'Назад' : 'Back'}
          </Button>
          <Button 
            onClick={confirmCancelSubscription} 
            variant="contained" 
            color="error"
            startIcon={<CancelSubscriptionIcon />}
          >
            {language === 'ua' ? 'Скасувати підписку' : 'Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};



export default SubscriptionPage;
